import { Form } from '@epages/react-components'
import type { Ref } from 'react'
import { createElement, useRef, useState } from 'react'
import Immutable from 'immutable'
import cc from 'classcat'
import loadable from '@loadable/component'

import ImageField, { withImageData, withMergedImageData } from './ImageField'
import { Banner } from '../../../../Banner/Banner'
import { EditorLink } from '../../../../EditorLink/EditorLink'
import type { ImageEditorRef } from '../../../../ImageEditor/ImageEditor'
import { renderImage as sharedRenderImage } from './imageRendering'
import { useEditorSpotlight } from '../../../../EditorSpotlight'
import { useTranslate } from '../../../../../utils/translate'
import CtaTextOverlay from '../components/CtaTextOverlay'
import useHelpCenterLink from '../../../../../utils/hooks/useHelpCenterLink'

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ '../../SettingsLayer'))
const Settings = loadable(() => import(/* webpackChunkName: "editor" */ './ImageSettings'))

const defaultData = Immutable.fromJS({
  src: '',
  width: null,
  height: null,
  alt: '',
  link: '',
  opentab: false,
  text: '',
  buttontext: '',
  headline: '',
  buttonenabled: false,

  // Reference to the original image (image before any modifications).
  // Initially, after image upload, this is always `undefined`. The property is
  // first set on first image editor "init" event. Before that, the "src"
  // property has the reference to the original image.
  // See `ImageEditor` use below.
  refSrc: '',

  // Image editing settings (used with the image editor).
  edit: {
    aspectRatio: { label: undefined, value: undefined },
    offset: [0, 0], // [x, y]
    zoom: 1,
  },
})

export const createEmptyImage = ({
  error,
  helpCenterUrl,
  t,
  data,
  onSave,
  onEdit,
  config,
  setError,
  setImageEditPreviewSrc,
  setIsInitialImageUploading,
  imagePlaceholderElementRef,
  isUnsupportedMediaType,
  isHeroImage,
}) => (
  <div className="plugin-placeholder" ref={imagePlaceholderElementRef}>
    <div className="dali-settingslayer-element">
      {error ? (
        <Banner type="danger" role="alert">
          {error.message}{' '}
          {isUnsupportedMediaType && (
            <EditorLink href={helpCenterUrl} type="external">
              {t('components.imageUploadComponent.imageField.errorMessages.helpCenterLink')}
            </EditorLink>
          )}
        </Banner>
      ) : null}
    </div>
    <Form
      name="pluginImagePlaceholder"
      value={withImageData(data)}
      prepare={withImageData}
      onChange={(data: ImmutableMap) => {
        setIsInitialImageUploading(false)
        onSave(withMergedImageData(data).remove('imageData'))
        onEdit('editImage')
      }}
      className="dali-form"
    >
      <ImageField
        withImageInfo
        name="imageData"
        onError={(error: Error) => {
          setIsInitialImageUploading(false)
          setError(error)
        }}
        onChange={() => setError(null)}
        onUploadPreview={(previewSrc: string) => {
          setError(null)
          setImageEditPreviewSrc(previewSrc)
          setIsInitialImageUploading(true)
        }}
        storeFile={config?.storeFile}
        editAspectRatio={data.getIn(['edit', 'aspectRatio', 'value'])}
        isHeroImage={isHeroImage}
      />
    </Form>
  </div>
)

export default function ImagePlugin({
  config,
  editorView,
  editorMode,
  editAction,
  isMultiColumn,
  data = defaultData,
  onDataChange,
  onSave,
  onCancel,
  onEdit,
  imageEditorRef,
}: Readonly<WorkspacePluginProps & { imageEditorRef?: Ref<ImageEditorRef> }>) {
  const [error, setError] = useState<Error | null>(null)
  const [imageEditPreviewSrc, setImageEditPreviewSrc] = useState<string>()
  const ref = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const helpCenterUrl = useHelpCenterLink('IMAGE_FORMATS')
  const [isActive, setIsActive] = useState(false)

  const t = useTranslate('interface')

  const isUnsupportedMediaType = error?.code === 415
  const isEmpty = !data.get('src')
  const isLinked = Boolean(data.get('link'))
  const pluginActiveClasses = cc([
    'dali-plugin-image',
    {
      'dali-grid-element-highlighted': isActive,
    },
  ])

  // Optimistically put the image placeholder element into spotlight while the
  // initial image is uploading and before the image editor is rendering at the
  // same position in spotlight. Depending on the internet connection, the
  // initial image upload can take a while and we don’t want the user to be
  // able to interact with other elements in the meantime.
  const imagePlaceholderElementRef = useRef<HTMLDivElement>(null)
  const [isInitialImageUploading, setIsInitialImageUploading] = useState(false)
  useEditorSpotlight(isInitialImageUploading ? imagePlaceholderElementRef.current : null)

  const isHeroImage = false

  const emptyImage = createEmptyImage({
    error,
    helpCenterUrl,
    t,
    data,
    onSave,
    onEdit,
    config,
    setError,
    setImageEditPreviewSrc,
    setIsInitialImageUploading,
    imagePlaceholderElementRef,
    isUnsupportedMediaType,
    isHeroImage,
  })

  /**
   * Renders the image in the storefront or in the editor. Renders in image
   * editor mode when the edit action is "editImage".
   *
   * The image editor uses the original image as the source for editing:
   *
   * - Initially, the "src" property is always set to the original image.
   * - After the first image editor "init" event, the "refSrc" property is set
   *   to the original image and the "src" property is set to the edited image.
   */
  function renderImage() {
    const aspectRatioMap = isMultiColumn ? multiColumnAspectRatioMap : singleColumnAspectRatioMap

    return sharedRenderImage({
      data,
      config,
      editorView,
      editAction,
      imageEditPreviewSrc,
      imageRef,
      aspectRatioMap,
      defaultData,
      onSave,
      onCancel,
      onDataChange,
      setImageEditPreviewSrc,
      imageEditorRef,
      OverlayComponent: CtaTextOverlay,
      overlayProps: {
        isCircle: data.getIn(['edit', 'aspectRatio', 'label']) === 'circle',
      },
      wrapperClassName: 'dali-image-wrapper',
      imageClassName: 'dali-image',
      imageStyle: {},
      isLinked,
    })
  }

  if (editorView) {
    return (
      <div className={pluginActiveClasses} ref={ref}>
        {isEmpty ? emptyImage : renderImage()}

        <SettingsLayer
          className="dali-settingslayer-image-plugin"
          onActiveStateChange={setIsActive}
          open={editorMode === 'edit' && editAction !== 'editImage'}
          virtualRef={ref}
          side="right"
          onEscapeKeyDown={onCancel}
        >
          {({ renderLayout }) => {
            return createElement(Settings, {
              data,
              onDataChange,
              config,
              onCancel,
              renderLayout,
              onSave: (data) => {
                setError(null)
                onSave(data)
              },
            })
          }}
        </SettingsLayer>
      </div>
    )
  }

  return !isEmpty ? <div className={pluginActiveClasses}>{renderImage()}</div> : null
}

export const singleColumnAspectRatioMap = {
  '3:1': 3 / 1,
  '3:2': 3 / 2,
  '4:3': 4 / 3,
  '5:1': 5 / 1,
  '16:9': 16 / 9,
}

export const multiColumnAspectRatioMap = {
  '1:1': 1,
  '2:3': 2 / 3,
  '3:1': 3 / 1,
  '3:2': 3 / 2,
  '4:3': 4 / 3,
  '16:9': 16 / 9,
  circle: 1,
}
